import { useState, useEffect, useCallback } from 'react';
import { ApiClient } from '../../../utils/apiClient';
import {
  Wallet,
  WalletTransaction,
  PaymentMethod,
  AutoRechargeSettings,
  UsageSummary,
  UsageRecord,
  PricingProfile,
  BillingFilters,
  TopUpProductType,
  DirectChargeResponse,
  CouponValidationResponse,
  PaginatedResponse,
} from '../types';

export const useBilling = () => {
  // State
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [autoRechargeSettings, setAutoRechargeSettings] = useState<AutoRechargeSettings | null>(
    null
  );
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [pricingProfile, setPricingProfile] = useState<PricingProfile | null>(null);
  const [filters, setFilters] = useState<BillingFilters>({
    search: '',
    status: '',
    dateRange: '',
    transactionType: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({
    count: 0,
    next: null,
    previous: null,
  });

  // Load wallet balance
  const loadWallet = useCallback(async () => {
    try {
      const response = await ApiClient.get('/billing/wallet/current/');
      if (response.ok) {
        const data = await response.json();
        setWallet(data);
      } else {
        throw new Error('Failed to load wallet');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load wallet');
    }
  }, []);

  // Load transactions
  const loadTransactions = useCallback(
    async (pageUrl?: string) => {
      try {
        let url = '/billing/wallet/transactions/';
        const params = new URLSearchParams();

        if (pageUrl) {
          // Extract path and query from pagination URL (handles both full URLs and relative paths)
          try {
            const urlObj = new URL(pageUrl, window.location.origin);
            // Extract pathname and search, removing the /api prefix if present
            const pathWithQuery = urlObj.pathname.replace(/^\/api/, '') + urlObj.search;
            url = pathWithQuery;
          } catch {
            // If pageUrl is already a relative path, use it as-is
            url = pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`;
          }
        } else {
          // Build URL with filters
          if (filters.transactionType) {
            params.append('transaction_type', filters.transactionType);
          }
          if (filters.dateRange) {
            const now = new Date();
            const daysAgo = parseInt(filters.dateRange);
            const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
            params.append('created_after', startDate.toISOString());
          }
          if (filters.search) {
            params.append('search', filters.search);
          }

          if (params.toString()) {
            url += `?${params.toString()}`;
          }
        }

        const response = await ApiClient.get(url);
        if (response.ok) {
          const data: PaginatedResponse<WalletTransaction> = await response.json();
          setTransactions(data.results);
          setPagination({
            count: data.count,
            next: data.next,
            previous: data.previous,
          });
        } else {
          throw new Error('Failed to load transactions');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load transactions');
      }
    },
    [filters]
  );

  // Load payment methods
  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await ApiClient.get('/billing/payment-methods/');
      if (response.ok) {
        const data: PaymentMethod[] = await response.json();
        setPaymentMethods(data);
      } else {
        throw new Error('Failed to load payment methods');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment methods');
    }
  }, []);

  // Load auto-recharge settings
  const loadAutoRechargeSettings = useCallback(async () => {
    try {
      const response = await ApiClient.get('/billing/auto-recharge/current/');
      if (response.ok) {
        const data = await response.json();
        setAutoRechargeSettings(data);
      } else if (response.status === 404) {
        setAutoRechargeSettings(null);
      } else {
        throw new Error('Failed to load auto-recharge settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load auto-recharge settings');
    }
  }, []);

  // Load usage summary
  const loadUsageSummary = useCallback(async (dateRange?: string) => {
    try {
      let url = '/billing/usage/summary/';
      if (dateRange) {
        const params = new URLSearchParams();
        const now = new Date();
        // Use local date to avoid timezone issues
        const localNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let startDate: Date;
        let endDate: Date = localNow;

        switch (dateRange) {
          case 'last':
            // First day of last month to last day of last month
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
            break;
          case 'quarter': {
            const quarter = Math.floor(now.getMonth() / 3);
            startDate = new Date(now.getFullYear(), quarter * 3, 1);
            break;
          }
          case 'year':
            startDate = new Date(now.getFullYear(), 0, 1);
            break;
          default:
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        // Format dates as YYYY-MM-DD without timezone conversion
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        params.append('date_from', formatDate(startDate));
        params.append('date_to', formatDate(endDate));
        url += `?${params.toString()}`;
      }

      const response = await ApiClient.get(url);
      if (response.ok) {
        const data = await response.json();
        setUsageSummary(data);
      } else if (response.status === 404) {
        // API endpoint not implemented yet, set to null
        setUsageSummary(null);
      } else {
        throw new Error('Failed to load usage summary');
      }
    } catch (err) {
      // Don't set error for 404s, just set summary to null
      if (err instanceof Error && !err.message.includes('404')) {
        setError(err.message);
      }
      setUsageSummary(null);
    }
  }, []);

  // Load usage records
  const loadUsageRecords = useCallback(async (dateRange?: string) => {
    try {
      let url = '/billing/usage/';
      if (dateRange) {
        const params = new URLSearchParams();
        const now = new Date();
        // Use local date to avoid timezone issues
        const localNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let startDate: Date;
        let endDate: Date = localNow;

        switch (dateRange) {
          case 'last':
            // First day of last month to last day of last month
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0); // Last day of previous month
            break;
          case 'quarter': {
            const quarter = Math.floor(now.getMonth() / 3);
            startDate = new Date(now.getFullYear(), quarter * 3, 1);
            break;
          }
          case 'year':
            startDate = new Date(now.getFullYear(), 0, 1);
            break;
          default:
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        // Format date as YYYY-MM-DD without timezone conversion
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        params.append('date_from', formatDate(startDate));
        params.append('date_to', formatDate(endDate));
        url += `?${params.toString()}`;
      }

      const response = await ApiClient.get(url);
      if (response.ok) {
        const data: PaginatedResponse<UsageRecord> = await response.json();
        setUsageRecords(data.results);
      } else if (response.status === 404) {
        // API endpoint not implemented yet, set to empty array
        setUsageRecords([]);
      } else {
        throw new Error('Failed to load usage records');
      }
    } catch (err) {
      // Don't set error for 404s, just set records to empty array
      if (err instanceof Error && !err.message.includes('404')) {
        setError(err.message);
      }
      setUsageRecords([]);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadWallet(),
        loadTransactions(),
        loadPaymentMethods(),
        loadAutoRechargeSettings(),
        loadUsageSummary('current'),
        loadUsageRecords('current'),
      ]);
      setLoading(false);
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload transactions when filters change
  useEffect(() => {
    loadTransactions();
  }, [filters, loadTransactions]);

  const validateCoupon = useCallback(
    async (couponCode: string, amount: string): Promise<CouponValidationResponse> => {
      try {
        const response = await ApiClient.post('/billing/payments/validate-coupon/', {
          coupon_code: couponCode.trim(),
          amount: amount,
        });

        if (response.ok) {
          const data: CouponValidationResponse = await response.json();
          return data;
        } else {
          const errorData = await response.json().catch(() => ({}));
          let errorMessage = 'Invalid coupon code';

          if (errorData && typeof errorData === 'object') {
            const error = (errorData as { error?: unknown }).error;
            if (typeof error === 'string') {
              errorMessage = error;
            } else if (Array.isArray(error)) {
              const firstError = error[0];
              if (typeof firstError === 'string') {
                errorMessage = firstError;
              } else if (firstError && typeof firstError === 'object' && 'string' in firstError) {
                errorMessage = (firstError as { string: string }).string;
              }
            }
          }

          return {
            valid: false,
            error: errorMessage,
          };
        }
      } catch (err) {
        return {
          valid: false,
          error: 'Failed to validate coupon code',
        };
      }
    },
    []
  );

  // Top-up: Direct charge using Payment Intent
  const handleTopUp = useCallback(
    async (productType: TopUpProductType, paymentMethodId: string, couponCode?: string) => {
      try {
        setError(null);
        setLoading(true);

        const requestBody: {
          product_type: TopUpProductType;
          payment_method_id: string;
          coupon_code?: string;
        } = {
          product_type: productType,
          payment_method_id: paymentMethodId,
        };

        // Only include coupon_code if it's provided and not empty
        if (couponCode && couponCode.trim()) {
          requestBody.coupon_code = couponCode.trim();
        }

        const response = await ApiClient.post('/billing/payments/charge/', requestBody);

        if (response.ok) {
          const data: DirectChargeResponse = await response.json();

          // Check if 3D Secure authentication is required
          if (data.requires_action && data.client_secret) {
            // Return the response so component can handle 3D Secure
            setLoading(false);
            return data;
          }

          // Payment succeeded immediately
          if (data.status === 'succeeded') {
            // Refresh wallet balance after a short delay to allow webhook processing
            setTimeout(async () => {
              await loadWallet();
            }, 1000);
            setLoading(false);
            return data;
          }

          // Other status
          setLoading(false);
          return data;
        } else {
          const errorData = await response.json();
          setLoading(false);
          throw new Error(errorData.error || errorData.detail || 'Payment failed');
        }
      } catch (err) {
        setLoading(false);
        setError(err instanceof Error ? err.message : 'Failed to process payment');
        throw err;
      }
    },
    [loadWallet]
  );

  // Save auto-recharge settings
  const saveAutoRechargeSettings = useCallback(
    async (settings: {
      status: 'enabled' | 'disabled';
      amount: string;
      threshold: string;
      payment_method?: string;
    }) => {
      try {
        setError(null);
        // Use /current/ endpoint for both create and update
        const response = autoRechargeSettings
          ? await ApiClient.patch('/billing/auto-recharge/current/', settings)
          : await ApiClient.post('/billing/auto-recharge/current/', settings);

        if (response.ok) {
          await loadAutoRechargeSettings();
          return { success: true };
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to save auto-recharge settings');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save auto-recharge settings');
        throw err;
      }
    },
    [autoRechargeSettings, loadAutoRechargeSettings]
  );

  // Set default payment method
  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      try {
        setError(null);
        const response = await ApiClient.post(
          `/billing/payment-methods/${paymentMethodId}/set-default/`
        );

        if (response.ok) {
          await loadPaymentMethods();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to set default payment method');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to set default payment method');
      }
    },
    [loadPaymentMethods]
  );

  // Delete payment method
  const removePaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      try {
        setError(null);

        // Check if this is the last active payment method
        const activeMethods = paymentMethods.filter(pm => pm.is_active);
        if (activeMethods.length <= 1) {
          throw new Error(
            'Cannot delete the last payment method. At least one payment method is required.'
          );
        }

        const response = await ApiClient.delete(`/billing/payment-methods/${paymentMethodId}/`);

        if (response.ok) {
          await loadPaymentMethods();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to remove payment method');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove payment method');
      }
    },
    [loadPaymentMethods, paymentMethods]
  );

  // Export transactions
  const exportTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.transactionType) {
        params.append('transaction_type', filters.transactionType);
      }
      if (filters.dateRange) {
        const now = new Date();
        const daysAgo = parseInt(filters.dateRange);
        const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
        params.append('created_after', startDate.toISOString());
      }

      const url = params.toString()
        ? `/billing/wallet/transactions/export/?${params.toString()}`
        : '/billing/wallet/transactions/export/';
      const response = await ApiClient.get(url);

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error('Failed to export transactions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export transactions');
    }
  }, [filters]);

  // Export usage
  const exportUsage = useCallback(async () => {
    try {
      setError(null);
      // Using the same pattern as transactions export
      // The endpoint should be /api/billing/usage/export/ (following the pattern of wallet/transactions/export/)
      const response = await ApiClient.get('/billing/usage/export/');
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `usage-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error('Failed to export usage');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export usage');
    }
  }, []);

  // Handle date range change for usage
  const handleUsageDateRangeChange = useCallback(
    (range: string) => {
      loadUsageSummary(range);
      loadUsageRecords(range);
    },
    [loadUsageSummary, loadUsageRecords]
  );

  // Load pricing profile
  const loadPricingProfile = useCallback(async () => {
    try {
      setError(null);
      const response = await ApiClient.get('/billing/pricing-profiles/current/');
      if (response.ok) {
        const data: PricingProfile = await response.json();
        setPricingProfile(data);
      } else if (response.status === 404) {
        setPricingProfile(null);
      } else {
        throw new Error('Failed to load pricing profile');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing profile');
      setPricingProfile(null);
    }
  }, []);

  // Check payment status (for after Stripe redirect)
  const checkPaymentStatus = useCallback(
    async (sessionId: string) => {
      try {
        const response = await ApiClient.get(`/billing/payments/status/?session_id=${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'succeeded') {
            await loadWallet();
            await loadTransactions();
          }
          return data;
        } else {
          throw new Error('Failed to check payment status');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to check payment status');
      }
    },
    [loadWallet, loadTransactions]
  );

  // Attach payment method
  const attachPaymentMethod = useCallback(
    async (paymentMethodId: string) => {
      try {
        setError(null);
        const response = await ApiClient.post('/billing/payment-methods/attach/', {
          payment_method_id: paymentMethodId,
        });
        if (response.ok) {
          await loadPaymentMethods();
          return await response.json();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to attach payment method');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to attach payment method');
        throw err;
      }
    },
    [loadPaymentMethods]
  );

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    wallet,
    transactions,
    paymentMethods,
    autoRechargeSettings,
    usageSummary,
    usageRecords,
    pricingProfile,
    filters,
    pagination,
    loading,
    error,

    // Actions
    setFilters,
    loadWallet,
    loadPaymentMethods,
    validateCoupon,
    handleTopUp,
    saveAutoRechargeSettings,
    setDefaultPaymentMethod,
    removePaymentMethod,
    exportTransactions,
    exportUsage,
    handleUsageDateRangeChange,
    checkPaymentStatus,
    loadTransactions: (pageUrl?: string) => loadTransactions(pageUrl),
    loadPricingProfile,
    clearError,
    attachPaymentMethod,
  };
};
